/**
 * ArubaOnboardingPage — clone of the Meraki onboarding flow, scoped to Aruba.
 *
 * Drives a Stepper based on per-step status; each step can be re-run.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  PlayCircle,
  RotateCw,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Stepper } from "@/components/ui/stepper";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaGroups } from "@/stores/aruba-groups";
import type { ArubaOnboardingStep, ArubaGroup } from "@/api/schemas/aruba";
import { MOCK_ARUBA_ONBOARDING } from "@/api/fixtures/aruba-onboarding";

function stateStyle(s: ArubaOnboardingStep["state"]) {
  switch (s) {
    case "ok":      return { pill: "ok"   as const, label: "complete", icon: <CheckCircle2 size={14} /> };
    case "running": return { pill: "info" as const, label: "running",  icon: <Clock size={14} /> };
    case "error":   return { pill: "err"  as const, label: "failed",   icon: <XCircle size={14} /> };
    default:        return { pill: "warn" as const, label: "pending",  icon: <Clock size={14} /> };
  }
}

export function ArubaOnboardingPage() {
  const { data: groups, load: loadGroups } = useArubaGroups();
  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  const [group, setGroup] = useState<ArubaGroup | null>(null);
  const [steps, setSteps] = useState<ArubaOnboardingStep[]>(MOCK_ARUBA_ONBOARDING);
  const [tickingSlug, setTickingSlug] = useState<string | null>(null);


  const activeIndex = useMemo(() => {
    const i = steps.findIndex((s) => s.state === "running" || s.state === "pending");
    return i === -1 ? steps.length - 1 : i;
  }, [steps]);

  const allOk = steps.every((s) => s.state === "ok");

  function rerunStep(slug: string) {
    setTickingSlug(slug);
    setSteps((prev) => prev.map((s) => (s.slug === slug ? { ...s, state: "running", detail: "re-running…" } : s)));
    setTimeout(() => {
      setSteps((prev) => prev.map((s) => (s.slug === slug ? { ...s, state: "ok", detail: "re-run succeeded" } : s)));
      setTickingSlug(null);
      toast.success("Step re-run completed");
    }, 1300);
  }

  function runAll() {
    if (!group) return;
    const pending = steps.filter((s) => s.state === "pending" || s.state === "running" || s.state === "error");
    if (pending.length === 0) {
      toast.info("Nothing to run");
      return;
    }
    toast.info(`Queued ${pending.length} step${pending.length === 1 ? "" : "s"}`);
    pending.forEach((s, i) => setTimeout(() => rerunStep(s.slug), i * 1100));
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">Aruba</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Onboarding</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Onboarding</h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Provisions an Aruba site against the GuestTek baseline. Each step is idempotent — re-runs are safe.
          </p>
        </div>
        {allOk && group ? (
          <Badge variant="ok" size="md">
            <CheckCircle2 size={11} />
            Onboarding complete
          </Badge>
        ) : null}
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Group</CardTitle>
              <CardSub>Pick the Aruba group to onboard.</CardSub>
            </div>
            <Button onClick={runAll} disabled={!group || allOk}>
              <PlayCircle size={14} />
              Run all pending
            </Button>
          </div>
          <select
            aria-label="Aruba group"
            value={group?.name ?? ""}
            onChange={(e) => {
              const g = groups.find((x) => x.name === e.target.value);
              setGroup(g ?? null);
              if (g) setSteps(MOCK_ARUBA_ONBOARDING);
            }}
            className="h-10 rounded-xl border border-line bg-surface px-3.5 text-sm text-fg focus-visible:border-violet/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
          >
            <option value="">— select a group —</option>
            {groups.map((g) => (
              <option key={g.name} value={g.name}>
                {g.name} · {g.architecture ?? "—"}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {group ? (
        <>
          <Card>
            <CardContent>
              <Stepper
                active={activeIndex}
                steps={steps.map((s) => ({ id: s.slug, title: s.title, hint: s.detail }))}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Step status</CardTitle>
                  <CardSub>
                    {steps.filter((s) => s.state === "ok").length}/{steps.length} complete
                  </CardSub>
                </div>
              </div>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {steps.map((s) => {
                  const sty = stateStyle(s.state);
                  const ticking = tickingSlug === s.slug;
                  return (
                    <li
                      key={s.slug}
                      className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 rounded-xl border border-line bg-surface p-3"
                    >
                      <span
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
                          sty.pill === "ok"
                            ? "bg-mint/15 text-mint"
                            : sty.pill === "info"
                              ? "bg-violet/15 text-violet"
                              : sty.pill === "err"
                                ? "bg-rose/15 text-rose"
                                : "bg-peach/15 text-peach"
                        }`}
                      >
                        {sty.icon}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-fg">{s.title}</div>
                        {s.detail ? <div className="text-[11px] text-fg-mute">{s.detail}</div> : null}
                      </div>
                      <Badge variant={sty.pill} size="sm">{sty.label}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => rerunStep(s.slug)} disabled={ticking}>
                        <RotateCw size={13} />
                        {ticking ? "…" : "Re-run"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CardTitle>Pick a group to begin</CardTitle>
            <CardSub>Onboarding status will populate as soon as you select one.</CardSub>
          </CardContent>
        </Card>
      )}

      {tickingSlug ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-violet/30 bg-violet/10 px-3.5 py-2.5 text-sm text-violet">
          <AlertTriangle size={16} />
          <span>Step <b className="font-mono">{tickingSlug}</b> running…</span>
        </output>
      ) : null}
    </div>
  );
}
